import type { DocNode } from '@atlaskit/adf-schema/doc';
import { traverse } from '@atlaskit/adf-utils/traverse';
import { type ADFEntity } from '@atlaskit/adf-utils/types';

function addChild(node: ADFEntity, type: string) {
	if (!node.content) {
		node.content = [{ type }];
	} else {
		node.content = [...node.content, { type }];
	}
	return node;
}

function addBreak(node: ADFEntity) {
	return addChild(node, 'hardBreak');
}

// Convert an ADF document to plain text
// This function take inspiration from [here](https://bitbucket.org/atlassian/pf-adf-service/src/master/src/lib/converters/adf-to-text.ts)
export default function adfToText(doc: DocNode): string {
	const result: string[] = [];

	traverse(doc, {
		text: (node) => {
			result.push(node.text || '');
		},
		blockquote: (node) => {
			result.push('> ');
			return addBreak(node);
		},
		codeBlock: (node) => {
			return addBreak(node);
		},
		date: (node) => {
			const timestamp = node.attrs?.timestamp;
			if (timestamp && timestamp.match(/^\d+$/)) {
				const date = new Date(Number(timestamp));
				result.push(date.toISOString().slice(0, 10));
			} else {
				result.push(timestamp);
			}
		},
		decisionItem: (node) => {
			result.push('<> ');
			return addBreak(node);
		},
		emoji: (node) => {
			const emoji = node.attrs?.text;
			if (emoji) {
				result.push(emoji);
			} else {
				result.push(node.attrs?.shortName || ':unknown:');
			}
		},
		hardBreak: () => {
			result.push('\n');
		},
		heading: (node) => {
			return addBreak(node);
		},
		inlineCard: (node) => {
			result.push(node.attrs?.url || ' ');
		},
		listItem: (node, parent, nodeIndex) => {
			if (parent.node!.type === 'orderedList') {
				const startIndex = parent.node!.attrs?.order || 1;
				const listIndex = nodeIndex + startIndex;
				result.push(listIndex + '. ');
			} else if (parent.node!.type === 'bulletList') {
				result.push('* ');
			}
			return addBreak(node);
		},
		mediaGroup: (node) => {
			result.push('📎 ' + (node.content?.length || 0) + ' File(s)');
			return addBreak(node);
		},
		mediaSingle: (node) => {
			result.push('[media]');
			return addBreak(node);
		},
		mention: (node) => {
			const name = node.attrs?.text;
			if (name) {
				result.push(name);
			} else {
				result.push('@unknown');
			}
		},
		paragraph: (node, parent) => {
			// Paragraphs collapse newlines when they coincide with other block elements
			// We know we have a parent node, and that parent has content because of the way the traverse function
			// traverses the node tree
			const lastSiblingType = parent.node!.content![parent.node!.content!.length - 1]!.type;
			if (lastSiblingType !== 'hardBreak' && lastSiblingType !== 'noBreak') {
				return addBreak(node);
			}
		},
		rule: () => {
			result.push('\n');
		},
		tableCell: (node) => {
			result.push('|');
			return addChild(node, 'noBreak');
		},
		tableHeader: (node) => {
			result.push('|');
			return addChild(node, 'noBreak');
		},
		tableRow: (node) => {
			return addBreak(addChild(node, 'tableRowTerminator'));
		},
		tableRowTerminator: () => {
			result.push('|');
		},
		taskItem: (node) => {
			if (node.attrs?.state === 'DONE') {
				result.push('[x] ');
			} else {
				result.push('[] ');
			}
			return addBreak(node);
		},
	});
	return result.join('').trim();
}
